import { overviewCache } from "./overviewCache";

export const REPORT_FRESH_MS = 300000;
export const REPORT_MAX_MS = 900000;

export function isReportRequest(url, config = {}) {
  if (config.reportCache === false || (config.responseType && config.responseType !== "json")) return false;
  const path = String(url).split("?")[0];
  if (/\/(export|download|workflow|assignment|history|settings|rules|impact|operations)(\/|$)/.test(path)) return false;
  return /^\/(analytics|portfolio)(\/|$)/.test(path) ||
    /^\/dashboard\/summary$/.test(path) ||
    /^\/cross-sell\/(dashboard|opportunities|options|performance|customers)(\/|$)/.test(path) ||
    /^\/renewals\/report$/.test(path) ||
    /^\/customers-report(\/|$)/.test(path) ||
    /^\/(policies|customers)$/.test(path) ||
    /^\/business-classification\/report$/.test(path) ||
    /^\/organization(\/tree)?$/.test(path);
}

export function stableKey(value) {
  if (Array.isArray(value)) return `[${value.map(stableKey).join(",")}]`;
  if (value && typeof value === "object") return `{${Object.keys(value).sort().map((key) =>
    `${JSON.stringify(key)}:${stableKey(value[key])}`).join(",")}}`;
  return JSON.stringify(value);
}

// A standalone client makes cache timing and cross-session races testable.
export function createReportClient({ get, generation, subscribe, token, route,
  notify = () => {}, now = Date.now, clone = (value) => JSON.parse(JSON.stringify(value)),
  canceled = () => new Error("Canceled"), maxEntries = 80, maxBytes = 20000000 }) {
  const entries = new Map();
  const refreshing = new Map();
  let bytes = 0;
  let sessionRequest;
  const clear = () => { entries.clear(); refreshing.clear(); bytes = 0; sessionRequest = null; };
  subscribe(clear);
  function store(key, response, expected) {
    if (generation() !== expected) return;
    const value = { data: clone(response.data), status: response.status,
      statusText: response.statusText, headers: { ...response.headers } };
    const size = JSON.stringify(value).length * 2;
    if (size > maxBytes) return;
    if (entries.has(key)) bytes -= entries.get(key).size;
    entries.delete(key);
    entries.set(key, { value, savedAt: now(), size });
    bytes += size;
    while (entries.size > maxEntries || bytes > maxBytes) {
      const first = entries.keys().next().value;
      bytes -= entries.get(first).size;
      entries.delete(first);
    }
  }
  async function session() {
    if (!sessionRequest) {
      const pending = get("/report-cache/context").catch((error) => {
        // A rolling frontend deployment must never cache against an old backend.
        if (error.response?.status !== 404) throw error;
        return get("/auth/me").then((response) => ({ data: { user: response.data, cacheable: false } }));
      });
      sessionRequest = pending;
      pending.finally(() => { if (sessionRequest === pending) sessionRequest = null; }).catch(() => {});
    }
    return sessionRequest;
  }
  return async (url, config = {}) => {
    if (!isReportRequest(url, config)) return get(url, config);
    const expected = generation();
    const currentToken = token();
    const authorized = await session();
    const valid = () => generation() === expected && token() === currentToken;
    if (!valid() || config.signal?.aborted) throw canceled();
    const key = stableKey([currentToken, authorized.data.user, authorized.data.revision,
      config.baseURL, url, config.params || {}]);
    const stillSameServerContext = async () => {
      const check = await get("/report-cache/context");
      return valid() && check.data.cacheable === true &&
        stableKey(check.data) === stableKey(authorized.data);
    };
    // Active uploads/background updates and old backends always bypass reuse.
    if (authorized.data.cacheable !== true) return get(url, config);
    const entry = entries.get(key);
    const age = entry ? now() - entry.savedAt : Infinity;
    if (entry && age < REPORT_MAX_MS) {
      const page = route();
      if (age >= REPORT_FRESH_MS && !refreshing.has(key)) {
        const job = {};
        refreshing.set(key, job);
        notify(page, key, "refreshing");
        // Keep refresh independent of the component that immediately receives a snapshot.
        const { signal, ...refreshConfig } = config;
        get(url, refreshConfig).then(async (response) => {
          if (!valid()) return;
          if (!await stillSameServerContext()) {
            if (valid()) notify(page, key, "error");
            return;
          }
          const changed = stableKey(entry.value.data) !== stableKey(response.data);
          store(key, response, expected);
          notify(page, key, changed ? "changed" : "idle");
        }).catch(() => {
          if (valid()) notify(page, key, "error");
        }).finally(() => { if (refreshing.get(key) === job) refreshing.delete(key); });
      }
      return { ...entry.value, data: clone(entry.value.data), config };
    }
    const response = await get(url, config);
    if (!valid() || config.signal?.aborted) throw canceled();
    if (await stillSameServerContext()) store(key, response, expected);
    return response;
  };
}

const statuses = new Map();
const listeners = new Set();
let reportContext = "";
export function setReportContext(context) { reportContext = context; }
export function reportStatus(page) {
  const values = [...(statuses.get(page)?.values() || [])];
  return values.includes("refreshing") ? "refreshing" : values.includes("error") ? "error" :
    values.includes("changed") ? "changed" : "idle";
}
export function subscribeReportStatus(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function clearReportStatus(page) {
  statuses.delete(page);
  listeners.forEach((listener) => listener());
}
function notify(page, key, state) {
  if (!statuses.has(page)) statuses.set(page, new Map());
  const current = statuses.get(page);
  if (state === "idle") current.delete(key);
  else current.set(key, state);
  while (statuses.size > 30) statuses.delete(statuses.keys().next().value);
  listeners.forEach((listener) => listener());
}
overviewCache.subscribe(() => { statuses.clear(); listeners.forEach((listener) => listener()); });

export function installReportCache(api, canceled) {
  const get = api.get.bind(api);
  api.get = createReportClient({ get, canceled,
    generation: overviewCache.generation, subscribe: overviewCache.subscribe,
    token: () => localStorage.getItem("imc_token"),
    route: () => window.location.pathname + window.location.search + "|" + reportContext, notify });
}
